import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Center,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  Image,
  Progress,
  Stack,
  useMediaQuery,
} from '@chakra-ui/react'
import { useEffect, useState, type Dispatch, type JSX, type SetStateAction } from 'react'
import { useDropzone, type DropEvent, type FileRejection } from 'react-dropzone'
import HtmlContainer from '../HtmlContainer'

import { CheckIcon, SmallCloseIcon } from '@chakra-ui/icons'
import { Box, Spacer, Text } from '@chakra-ui/layout'
import { Icon } from '@chakra-ui/react'
import type { AxiosProgressEvent } from 'axios'
import _ from 'lodash'
import * as API from '../../api'
import { uploadIcon } from '../../assets/svg/'
import { useUserAwareTranslation } from '../../i18n/hooks'
import { ButtonPrimary, Input, Select } from '../Base'
import { PendingImageCard } from '../Cards'
import type { UploadedFile } from './common'

const LIMIT = parseInt(import.meta.env.VITE_REACT_APP_UPLOAD_LIMIT || '5')

type ImageType = string | ArrayBuffer | null

function imagePropertyToString(image: ImageType): string {
  if (_.isUndefined(image)) {
    return ''
  }
  if (_.isNull(image)) {
    return ''
  }
  if (_.isString(image)) {
    return image
  }
  return new TextDecoder().decode(image)
}

interface ImageFormPropsData {
  subject: string
  author: string
}
interface ImageFormProps {
  data?: ImageFormPropsData
  user: API.APIUser
  onClose: () => unknown
  setFiles: Dispatch<SetStateAction<UploadedFile[]>>
  setStep: (step: number) => unknown
}

interface FileProgressInfo {
  fileName: string
  percentage: number
  icon?: JSX.Element
}

const ImageForm = ({
  data,
  user,
  onClose = () => {
    return undefined
  },
  setStep = () => {
    return undefined
  },
  setFiles = () => {
    return undefined
  },
}: ImageFormProps) => {
  const { t } = useUserAwareTranslation(user)
  const [language, setLanguage] = useState('en-US')
  const [uploadError, setUploadError] = useState('')
  const [selectedFiles, setSelectedFiles] = useState([] as File[])
  const [progressInfos, setProgressInfos] = useState([] as FileProgressInfo[])
  const [pending, setPending] = useState([] as UploadedFile[])
  const [subjects, setSubjects] = useState([] as API.OptionList[])
  const [subject, setSubject] = useState('')
  const [author, setAuthor] = useState('')
  const [fileProgressInfo, setFileProgressInfo] = useState(undefined as unknown as FileProgressInfo)
  const [tmpImage, setTmpImage] = useState(undefined as unknown as ImageType)
  const [isLoading, setIsloading] = useState(false)
  const [uploaded, setUploaded] = useState(false)
  const [isDisabled, setIsDisabled] = useState(false)
  const [isNext, setIsNext] = useState(false)
  const [isMobile] = useMediaQuery('(max-width: 479px)')
  const [isWithSmallHeight] = useMediaQuery('(max-height: 730px)')

  const { getRootProps, getInputProps } = useDropzone({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onDrop: (files: File[], _rejections: FileRejection[], _event: DropEvent) => {
      setUploadError('')
      if (files.length > 0) {
        setSelectedFiles(files)
      }
    },
    onDropRejected: (arr) => {
      reset()
      setUploadError(_.get(arr[0], 'errors[0].code'))
    },
    //renameFile: (file) => {
    //  const { name } = file
    //  return name + '_' + Date.now()
    //},
    multiple: false,
    maxSize: LIMIT * 1048576 || 5242880,
    maxFiles: 1,
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
    },
    noDragEventsBubbling: true,
  })

  const processImage = (file: Blob, cb: (data: string | ArrayBuffer | null) => unknown) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => cb(reader.result)
    reader.onerror = () => {
      return undefined
    } //(error) => console.log('[ImageForm.processImage]', error)
  }

  const updateProgress = () => {
    if (fileProgressInfo) {
      let _progressInfos = [...progressInfos]
      const percentage: number = _.get(fileProgressInfo, 'percentage', 0)
      let icon: JSX.Element | undefined = _.get(fileProgressInfo, 'icon')
      if (!icon && percentage === 100) {
        icon = <Icon as={CheckIcon} w={4} h={4} color="green.500" />
      } else if (!icon && percentage < 100) {
        icon = undefined
      }
      const p: FileProgressInfo = {
        fileName: fileProgressInfo.fileName,
        percentage,
        icon: icon ? (
          icon
        ) : percentage === 100 ? (
          <Icon as={CheckIcon} w={4} h={4} color="green.500" />
        ) : undefined,
      }
      const index = _.findIndex(_progressInfos, { fileName: _.get(fileProgressInfo, 'fileName') })
      if (index !== -1) {
        _progressInfos[index] = p
      } else {
        _progressInfos = [p, ...progressInfos]
      }
      setProgressInfos(_progressInfos)
    }
  }

  useEffect(() => {
    setFiles(pending)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending])

  const deletePendingFile = (id: string) => {
    const arr = pending.filter((p: { id: string }) => id !== p.id)
    setPending(arr)
    if (_.isEmpty(arr)) {
      reset()
    }
  }

  const upload = async (/*idx: number,*/ file: File, metadata: API.UploadMetadata) => {
    try {
      const uploadResult = await API.upload(
        user.token,
        { file, userId: user.inspecId, metadata },
        (event: AxiosProgressEvent) => {
          setFileProgressInfo({
            fileName: file.name,
            percentage:
              event.total && event.total != 0 ? Math.round((100 * event.loaded) / event.total) : 0,
          })
        }
      )
      if (!_.isUndefined(uploadResult.file)) {
        processImage(file, (image) =>
          setPending([
            ...pending,
            {
              id: _.get(uploadResult, 'file.Id', ''),
              image: imagePropertyToString(image),
              title: file.name,
              subject: `${t(
                _.get(
                  subjects.filter((s) => s.identifier === _.get(metadata, 'subject'))[0],
                  'identifier'
                )
              )}`,
              otherProvider: '',
            },
          ])
        )

        if (uploadResult.error) {
          setFileProgressInfo({
            fileName: file.name,
            percentage: 0,
            icon: <Icon as={SmallCloseIcon} w={4} h={4} color="red.500" />,
          })
        } else {
          setFileProgressInfo({
            fileName: file.name,
            percentage: 100,
          })
        }
      }
      if (uploadResult.error) {
        setUploadError(uploadResult.error)
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e: unknown) {
      setFileProgressInfo({
        fileName: file.name,
        percentage: 0,
        icon: <Icon as={SmallCloseIcon} w={4} h={4} color="red.500" />,
      })
    }
  }

  useEffect(() => {
    updateProgress()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileProgressInfo])

  useEffect(() => {
    setIsDisabled(
      // isEmpty(title) ||
      _.isEmpty(subject) || _.isEmpty(selectedFiles) || isLoading
    )
  }, [
    // title,
    subject,
    selectedFiles,
    isLoading,
  ])

  const uploadFiles = async () => {
    setIsloading(true)
    for (const selectedFile of selectedFiles) {
      //(let i = 0; i < selectedFiles.length; i++) {
      await upload(/*i,*/ selectedFile, {
        // title,
        subject,
        quality: 'MTP.Quality.QUA_PREMIUM',
        relateddInspecId: _.get(user, 'inspecId', ''),
        author,
        provider: 'MTP.Provider.PROV_AUTRE',
        otherProvider: _.get(user, 'poiName', ''),
      })
    }
    setIsloading(false)
    setUploaded(true)
  }

  const getSubjectOptions = async (user: API.APIUser) => {
    const result = await API.getSubjectOptionsList(user, language)
    setSubjects(result)
  }

  const getData = async () => {
    await getSubjectOptions(user)
  }

  const reset = () => {
    setSelectedFiles([])
    setFileProgressInfo(undefined as unknown as FileProgressInfo)
    setProgressInfos([])
    setUploaded(false)
    setIsNext(false)
    setTmpImage(undefined as unknown as ImageType)
    setSubject('')
    setAuthor('')
    setUploadError('')
  }

  const close = () => {
    reset()
    onClose()
    setStep(0)
    setPending([])
  }
  const cancel = () => {
    if (!_.isEmpty(pending)) {
      reset()
      setUploaded(true)
      setIsNext(true)
    } else {
      close()
    }
  }

  useEffect(() => {
    getData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (data) {
      setSubject(_.get(data, 'subject', ''))
      setAuthor(_.get(data, 'author', ''))
    }

    if (user) {
      setLanguage(_.get(user, 'spokenLanguages[0]'))
    }
  }, [data, user])

  return (
    <>
      {!_.isEmpty(uploadError) && (
        <Alert status="error" mb="10px">
          <AlertIcon />
          <AlertTitle mr={2}>{t('upload-error')}</AlertTitle>
          <AlertDescription>{t('' + uploadError, { limit: LIMIT })}</AlertDescription>
        </Alert>
      )}

      {!_.isEmpty(progressInfos) || isNext ? (
        <>
          {pending &&
            pending.map((p, i) => (
              <PendingImageCard
                user={user}
                key={`${i}-${p.id}`}
                id={p.id}
                image={imagePropertyToString(p.image)}
                subject={p.subject}
                title={p.title}
                deleteAction={deletePendingFile}
              />
            ))}
          {isLoading &&
            progressInfos.map((progressInfo, index) => (
              <Flex key={index}>
                <Box flex={1}>
                  <Text>{progressInfo.fileName}</Text>
                  <Progress hasStripe value={progressInfo.percentage || 0} colorScheme="red" />
                </Box>
                <Box pl={4} pt={4}>
                  {progressInfo.icon && <>{progressInfo.icon}</>}
                </Box>
              </Flex>
            ))}
        </>
      ) : (
        <Flex direction={['column', 'row']}>
          <Box w={['100%', '50%']}>
            <section className="container">
              <div {...getRootProps({ className: 'dropzone' })}>
                <Box cursor="pointer">
                  <Center h={['108px', '263px']} borderRadius={4} border="2px dashed #CBD5E0">
                    <input {...getInputProps()} />
                    {selectedFiles && Array.isArray(selectedFiles) && selectedFiles.length ? (
                      <Box>
                        {selectedFiles.length > 3 ? (
                          <Text>{`${selectedFiles.length} files`}</Text>
                        ) : (
                          selectedFiles.map((file) => {
                            processImage(file, setTmpImage)
                            const sizeInMB = (_.get(file, 'size', 0) / (1024 * 1024)).toFixed(2)
                            return (
                              <Box
                                justifyContent={'center'}
                                alignContent={'center'}
                                key={_.get(file, 'name')}
                                borderRadius={4}
                                w={['100px', '300px']}
                                h={['65px', '220px']}
                                boxShadow="lg">
                                <Image
                                  borderTopRadius={4}
                                  borderBottomRadius={[4, 0]}
                                  src={imagePropertyToString(tmpImage)}
                                  objectFit="cover"
                                  w="100%"
                                  h={['65px', '190px']}
                                  mb={['0', '5px']}
                                />
                                <Flex display={['none', 'flex']} justifyContent={'space-between'}>
                                  <Text
                                    isTruncated
                                    fontSize={'12px'}
                                    fontWeight={'700'}
                                    maxW={['90px', '200px']}
                                    ml="5px">
                                    {_.get(file, 'name')}
                                  </Text>
                                  <Text isTruncated fontSize={'12px'} maxW="50px" mr="5px">
                                    {`${sizeInMB} MB`}
                                  </Text>
                                </Flex>
                              </Box>
                            )
                          })
                        )}
                      </Box>
                    ) : (
                      <Stack spacing={1}>
                        <Image src={uploadIcon} boxSize={['40px', '65px']} />
                      </Stack>
                    )}
                  </Center>
                </Box>
              </div>
            </section>
            <Stack spacing={1}>
              <Text textAlign="center" fontWeight="400" fontSize={14} color="michelinPrimary">
                {t('darg-and-drop-msg')}
              </Text>
              <Text textAlign="center" fontWeight="400" fontSize={14} color="michelinPrimary">
                {t('image-type-msg')}
              </Text>
            </Stack>
          </Box>

          <Box pl={[0, 5]} pt={[5, 0]} w={['100%', '50%']}>
            <Grid templateColumns={['repeat(1, 1fr)', 'repeat(1, 1fr)']} gap={6}>
              <FormControl isRequired>
                <FormLabel>{t('upload-form-subject')}</FormLabel>
                <Select
                  placeholder={t('upload-form-subject')}
                  onChange={(e) => setSubject(e.target.value)}
                  value={subject}>
                  {subjects.map((s) => (
                    <option key={s.identifier} value={s.identifier}>
                      {t(s.identifier)}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>{t('upload-form-author')}</FormLabel>
                <Input
                  placeholder={t('upload-form-author')}
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                />
                <FormHelperText>{t('upload-form-author-help-text')}</FormHelperText>
              </FormControl>
            </Grid>
            <Flex pt="20px">
              <HtmlContainer html={t('mandatory-fields')} />
            </Flex>
          </Box>
        </Flex>
      )}
      <Flex direction={['column', 'row']} mb={4}>
        <Spacer />
        {!uploaded ? (
          <>
            <ButtonPrimary
              mt={4}
              w={['100%', '190px']}
              mr={[0, 3]}
              isLoading={isLoading}
              onClick={cancel}
              reverse={true}>
              {t('cancel')}
            </ButtonPrimary>
            <ButtonPrimary
              mt={4}
              w={['100%', '190px']}
              reverse={false}
              isDisabled={isDisabled}
              isLoading={isLoading}
              loadingText={t('validate-loading-text')}
              onClick={uploadFiles}>
              {t('validate')}
            </ButtonPrimary>
          </>
        ) : (
          <>
            <ButtonPrimary
              mt={4}
              w={['100%', '190px']}
              mr={[0, 3]}
              isLoading={isLoading}
              onClick={close}
              reverse={true}>
              {t('cancel-all')}
            </ButtonPrimary>
            <ButtonPrimary
              mt={4}
              w={['100%', '190px']}
              reverse={false}
              mr={[0, 3]}
              onClick={() => {
                reset()
                window.scrollTo(0, isMobile ? 150 : isWithSmallHeight ? 150 : 0)
              }}>
              {t('add-image-btn')}
            </ButtonPrimary>
            <ButtonPrimary
              mt={4}
              w={['100%', '190px']}
              reverse={false}
              variant="ghost"
              onClick={() => {
                setStep(1)
                reset()
              }}>
              {t('finalize')}
            </ButtonPrimary>
          </>
        )}
      </Flex>
    </>
  )
}

export default ImageForm
